import type { TimelineFragment } from '@/lib/types/datocms/graphql/generated'
import Image from 'next/image'
import clsx from 'clsx'

export default function TimelineCard({
	title,
	description,
	image,
	subtitle,
	activeCard,
	idx,
	onCardClick,
}: Readonly<TimelineFragment> & {
	activeCard: number
	idx: number
	onCardClick: (index: number) => void
}) {
	// Calculate animation delay based on distance from active card
	const getAnimationDelay = () => {
		const distance = Math.abs(idx - activeCard)
		return `${distance * 0.3}s` // 0.3s delay for each card distance
	}

	return (
		<div
			onClick={() => onCardClick(idx)}
			style={
				{
					'--timeline-delay': getAnimationDelay(),
				} as React.CSSProperties
			}
			className={clsx(
				'timeline-card carousel-card flex flex-col min-w-[300px] @lg:min-w-[455px] relative overflow-hidden pr-2.5 md:pr-5 text-left cursor-pointer',
				'transition-all duration-300',
				// dot
				"before:content-[''] before:absolute before:left-0 before:top-[calc(50%-32px)] before:-translate-y-full before:w-2.5 before:h-2.5 before:rounded-full before:transition-colors before:duration-300",
				// blue line
				`after:content-[" "] after:absolute after:left-0 after:top-[calc(50%-32px)] after:-translate-y-[6px] after:-translate-x-full after:w-full after:h-px after:bg-blue after:transition-[transform,opacity] after:duration-700 after:ease-in-out after:origin-left`,
				idx < activeCard &&
					'after:translate-x-0 after:opacity-100 [&]:after:delay-[var(--timeline-delay)]',
				idx <= activeCard
					? 'before:bg-blue [&]:before:delay-[var(--timeline-delay)]'
					: 'before:bg-blue-dark'
			)}
		>
			{image?.[0]?.url && (
				<div
					className={clsx(
						'relative bg-white-frosted overflow-hidden rounded-lg w-full max-w-[325px] h-[200px] transition-opacity duration-700',
						idx !== activeCard && 'opacity-40'
					)}
				>
					<Image
						src={image?.[0]?.url}
						alt={image?.[0]?.alt ?? 'Timeline Image'}
						width={250}
						height={250}
						className="object-cover w-full h-full"
						sizes="(max-width: 768px) 95vw, 325px"
					/>
				</div>
			)}
			<div
				className={clsx(
					'transition-opacity duration-700 max-w-[355px]',
					idx !== activeCard && 'opacity-40'
				)}
			>
				{title && <p className="text-5xl @xl:text-7xl mt-16">{title}</p>}
				{subtitle && <p className="text-xl font-medium mt-6">{subtitle}</p>}
				{description && <p className="@lg:text-lg text-blue-dark/70 mt-4">{description}</p>}
			</div>
		</div>
	)
}
